import React, { useCallback, useEffect, useRef, useState } from 'react';
import { apiService, type MediaItem } from '../../../services/api';
import { useLocale } from '../../../locales';

interface VideoLibraryModalProps {
  onClose: () => void;
  onSelect: (mediaItem: MediaItem) => void;
  currentUrl?: string;
}

// Tree node types
interface FolderNode {
  name: string;
  type: 'folder';
  path: string;
  children: (FolderNode | FileNode)[];
}

interface FileNode {
  name: string;
  type: 'file';
  path: string;
  mediaItem: MediaItem;
}

type TreeNode = FolderNode | FileNode;

// Helper function to check if a node is a folder
const isFolder = (node: TreeNode): node is FolderNode => node.type === 'folder';

// Build tree structure from API response
const buildTreeFromData = (data: any): FolderNode[] => {
  const roots: FolderNode[] = [];

  // Process rootImages (root level video files)
  if (data.rootImages && Array.isArray(data.rootImages)) {
    const videoFiles = data.rootImages.filter((item: MediaItem) => item.type === 'video');
    if (videoFiles.length > 0) {
      roots.push({
        name: 'rootImages',
        type: 'folder',
        path: 'rootImages',
        children: videoFiles.map((item: MediaItem) => ({
          name: item.name || item.file_name || item.url,
          type: 'file',
          path: `rootImages/${item.id || item.url}`,
          mediaItem: item,
        })),
      });
    }
  }

  // Process nested categories (e.g., videolar/images)
  Object.keys(data).forEach((key) => {
    if (key === 'rootImages') return;

    const categoryData = data[key];

    if (typeof categoryData === 'object' && categoryData !== null && !Array.isArray(categoryData)) {
      // This is a nested category like "videolar": { "images": [...] }
      const folder: FolderNode = {
        name: key,
        type: 'folder',
        path: key,
        children: [],
      };

      Object.keys(categoryData).forEach((subKey) => {
        const subData = categoryData[subKey];

        // Check for "images" key which contains the video files
        if (subKey === 'images' && Array.isArray(subData)) {
          const videoFiles = subData.filter((item: MediaItem) => item.type === 'video');
          const subFolder: FolderNode = {
            name: subKey,
            type: 'folder',
            path: `${key}/${subKey}`,
            children: videoFiles.map((item: MediaItem) => ({
              name: item.name || item.file_name || item.url,
              type: 'file',
              path: `${key}/${subKey}/${item.id || item.url}`,
              mediaItem: item,
            })),
          };
          folder.children.push(subFolder);
        }
      });

      if (folder.children.length > 0) {
        roots.push(folder);
      }
    }
  });

  return roots;
};

const VideoLibraryModal: React.FC<VideoLibraryModalProps> = ({
  onClose,
  onSelect,
  currentUrl,
}) => {
  const { t } = useLocale();
  const contentRef = useRef<HTMLDivElement>(null);
  const videoPreviewRefs = useRef<Map<string, HTMLVideoElement>>(new Map());
  const [treeData, setTreeData] = useState<FolderNode[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPath, setCurrentPath] = useState<string[]>([]);
  const [hoveredVideoUrl, setHoveredVideoUrl] = useState<string | null>(null);
  const [selectedVideoUrl, setSelectedVideoUrl] = useState<string | null>(null);

  useEffect(() => {
    contentRef.current?.focus();
  }, []);

  // Load saved folder path from localStorage on mount
  useEffect(() => {
    const savedPath = localStorage.getItem('video-library:last-path');
    if (savedPath) {
      try {
        const parsedPath = JSON.parse(savedPath);
        setCurrentPath(parsedPath);
      } catch {
        // Invalid JSON, ignore
      }
    }
  }, []);

  // Save current folder path to localStorage whenever it changes
  useEffect(() => {
    if (currentPath.length > 0) {
      localStorage.setItem('video-library:last-path', JSON.stringify(currentPath));
    } else {
      localStorage.removeItem('video-library:last-path');
    }
  }, [currentPath]);

  // Navigate to a folder by path
  const navigateToFolder = useCallback((path: string[]) => {
    setCurrentPath(path);
  }, []);

  useEffect(() => {
    const listener = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        // Stop all video previews
        videoPreviewRefs.current.forEach((video) => {
          video.pause();
          video.currentTime = 0;
        });
        setHoveredVideoUrl(null);
        setSelectedVideoUrl(null);

        // If we're in a subfolder, go back; otherwise close modal
        if (currentPath.length > 0) {
          navigateToFolder(currentPath.slice(0, -1));
        } else {
          onClose();
        }
      }
    };

    window.addEventListener('keydown', listener);
    return () => window.removeEventListener('keydown', listener);
  }, [onClose, currentPath, navigateToFolder]);

  useEffect(() => {
    const fetchMedia = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const response: any = await (apiService as any).request('/media-library/all-video');

        if (response?.data) {
          const tree = buildTreeFromData(response.data);
          setTreeData(tree);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : t('videoLibrary.loadError'));
      } finally {
        setIsLoading(false);
      }
    };

    fetchMedia();
  }, []);

  const handleSelect = useCallback(
    (item: MediaItem) => {
      // Stop all video previews
      videoPreviewRefs.current.forEach((video) => {
        video.pause();
        video.currentTime = 0;
      });
      setHoveredVideoUrl(null);
      setSelectedVideoUrl(null);
      onSelect(item);
      onClose();
    },
    [onSelect, onClose]
  );

  const handleVideoMouseEnter = useCallback((url: string) => {
    setHoveredVideoUrl(url);
    const videoEl = videoPreviewRefs.current.get(url);
    if (videoEl) {
      videoEl.currentTime = 0;
      videoEl.play().catch((err) => {
        console.error('Video preview play error:', err);
      });
    }
  }, []);

  const handleVideoMouseLeave = useCallback((url: string) => {
    setHoveredVideoUrl(null);
    const videoEl = videoPreviewRefs.current.get(url);
    if (videoEl) {
      videoEl.pause();
      videoEl.currentTime = 0;
    }
  }, []);

  const handleVideoClick = useCallback((url: string) => {
    setSelectedVideoUrl((prev) => {
      if (prev === url) {
        // Deselect: pause and reset
        const videoEl = videoPreviewRefs.current.get(url);
        if (videoEl) {
          videoEl.pause();
          videoEl.currentTime = 0;
        }
        return null;
      } else {
        // Select new video: pause all others
        videoPreviewRefs.current.forEach((video, videoUrl) => {
          if (videoUrl !== url) {
            video.pause();
            video.currentTime = 0;
          }
        });
        // Play selected video
        const videoEl = videoPreviewRefs.current.get(url);
        if (videoEl) {
          videoEl.currentTime = 0;
          videoEl.play().catch((err) => {
            console.error('Video play error:', err);
          });
        }
        return url;
      }
    });
  }, []);

  // Get the current folder contents
  const getCurrentContents = useCallback((): { folders: FolderNode[]; files: FileNode[] } => {
    let currentNodes: TreeNode[] = treeData;

    // Navigate through the path
    for (const pathSegment of currentPath) {
      const folder = currentNodes.find(
        (node) => isFolder(node) && node.name === pathSegment
      ) as FolderNode | undefined;
      if (folder) {
        currentNodes = folder.children;
      }
    }

    // Separate folders and files
    const folders = currentNodes.filter(isFolder);
    const files = currentNodes.filter((node) => !isFolder(node)) as FileNode[];

    return { folders, files };
  }, [treeData, currentPath]);

  const { folders, files } = getCurrentContents();

  const handleFolderClick = useCallback((folder: FolderNode) => {
    // Stop all video previews when navigating to folder
    videoPreviewRefs.current.forEach((video) => {
      video.pause();
      video.currentTime = 0;
    });
    setHoveredVideoUrl(null);
    setSelectedVideoUrl(null);
    navigateToFolder([...currentPath, folder.name]);
  }, [currentPath, navigateToFolder]);

  return (
    <div className="qe-modal" role="presentation">
      <div className="qe-modal__backdrop" onClick={onClose} />
      <div className="qe-modal__content qe-modal__content--video-library" ref={contentRef} tabIndex={-1}>
        <div className="qe-modal__header">
          <div className="qe-modal__header-content">
            <h2 className="qe-modal__title">{t('videoLibrary.title')}</h2>
            <p className="qe-modal__subtitle">{t('videoLibrary.subtitle')}</p>
          </div>
          <button
            type="button"
            className="qe-button qe-button--ghost qe-button--icon"
            onClick={onClose}
            aria-label={t('videoLibrary.close')}
          >
            ✕
          </button>
        </div>

        <div className="qe-modal__body">
          {isLoading ? (
            <div className="qe-video-library__loading">
              <p>{t('videoLibrary.loading')}</p>
            </div>
          ) : error ? (
            <div className="qe-video-library__error">
              <p>{error}</p>
            </div>
          ) : treeData.length === 0 ? (
            <div className="qe-video-library__empty">
              <p>{t('videoLibrary.empty')}</p>
            </div>
          ) : (
            <>
              {/* Breadcrumb Navigation */}
              <div className="qe-file-browser__breadcrumb">
                <button
                  className="qe-file-browser__breadcrumb-item"
                  onClick={() => navigateToFolder([])}
                >
                  🏠 {t('videoLibrary.rootFolder')}
                </button>
                {currentPath.map((segment, index) => (
                  <React.Fragment key={segment}>
                    <span className="qe-file-browser__breadcrumb-separator">/</span>
                    <button
                      className="qe-file-browser__breadcrumb-item"
                      onClick={() => navigateToFolder(currentPath.slice(0, index + 1))}
                    >
                      {segment}
                    </button>
                  </React.Fragment>
                ))}
              </div>

              {/* Current Folder Contents */}
              <div className="qe-file-browser__content">
                {folders.length === 0 && files.length === 0 ? (
                  <div className="qe-file-browser__empty">
                    <p>{t('videoLibrary.folderEmpty')}</p>
                  </div>
                ) : (
                  <>
                    {/* Folders */}
                    {folders.map((folder) => (
                      <div
                        key={folder.path}
                        className="qe-file-browser__item qe-file-browser__item--folder"
                        onClick={() => handleFolderClick(folder)}
                        onDoubleClick={() => handleFolderClick(folder)}
                      >
                        <span className="qe-file-browser__icon">📁</span>
                        <span className="qe-file-browser__name">{folder.name}</span>
                        <span className="qe-file-browser__count">({folder.children.length})</span>
                        <span className="qe-file-browser__arrow">→</span>
                      </div>
                    ))}

                    {/* Files - Video Grid */}
                    <div className="qe-video-library__grid">
                      {files.map((file) => {
                        const isSelected = currentUrl === file.mediaItem.url;
                        const isHovered = hoveredVideoUrl === file.mediaItem.url;
                        const isSelectedPreview = selectedVideoUrl === file.mediaItem.url;
                        return (
                          <div
                            key={file.path}
                            className={`qe-video-library__card ${
                              isSelected ? 'qe-video-library__card--selected' : ''
                            }`}
                          >
                            {/* Video Thumbnail/Preview */}
                            <div
                              className="qe-video-library__thumbnail"
                              onMouseEnter={() => handleVideoMouseEnter(file.mediaItem.url)}
                              onMouseLeave={() => handleVideoMouseLeave(file.mediaItem.url)}
                            >
                              <video
                                ref={(el) => {
                                  if (el) {
                                    videoPreviewRefs.current.set(file.mediaItem.url, el);
                                  }
                                }}
                                className="qe-video-library__preview"
                                src={file.mediaItem.url}
                                muted
                                loop
                                playsInline
                                preload="metadata"
                                onClick={() => handleVideoClick(file.mediaItem.url)}
                              />
                              {/* Play overlay */}
                              <div
                                className={`qe-video-library__play-overlay ${
                                  (isHovered || isSelectedPreview) ? 'qe-video-library__play-overlay--hidden' : ''
                                }`}
                                onClick={() => handleVideoClick(file.mediaItem.url)}
                              >
                                <svg width="32" height="32" viewBox="0 0 16 16" fill="currentColor">
                                  <path d="M4 2L12 8L4 14V2Z" />
                                </svg>
                              </div>
                              {/* Selected indicator */}
                              {isSelected && (
                                <div className="qe-video-library__selected-indicator">
                                  <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                                    <path d="M10 0C4.48 0 0 4.48 0 10C0 15.52 4.48 20 10 20C15.52 20 20 15.52 20 10C20 4.48 15.52 0 10 0ZM8 15L3 10L4.41 8.59L8 12.17L15.59 4.58L17 6L8 15Z" />
                                  </svg>
                                </div>
                              )}
                            </div>

                            {/* Video Info */}
                            <div className="qe-video-library__info">
                              <div
                                className="qe-video-library__name"
                                title={file.name}
                                onClick={() => handleSelect(file.mediaItem)}
                              >
                                {file.name}
                              </div>
                              {file.mediaItem.size_formatted && (
                                <div className="qe-video-library__size">{file.mediaItem.size_formatted}</div>
                              )}
                              {/* Select button */}
                              <button
                                type="button"
                                className={`qe-video-library__select-btn ${
                                  isSelected ? 'qe-video-library__select-btn--selected' : ''
                                }`}
                                onClick={() => handleSelect(file.mediaItem)}
                              >
                                {isSelected ? t('videoLibrary.selected') : t('videoLibrary.select')}
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>
            </>
          )}
        </div>

        <div className="qe-modal__footer">
          {currentPath.length > 0 && (
            <button
              type="button"
              className="qe-button qe-button--secondary"
              onClick={() => navigateToFolder(currentPath.slice(0, -1))}
            >
              {t('videoLibrary.back')}
            </button>
          )}
          <button
            type="button"
            className="qe-button qe-button--primary"
            onClick={onClose}
          >
            {t('videoLibrary.cancel')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default VideoLibraryModal;
